import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Card, CardContent, Avatar, Button, Grid, Chip, List, ListItem, ListItemAvatar, ListItemText, TextField, InputAdornment, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FarmAvatar from '../components/FarmAvatar';
import { useAuth } from '../contexts/AuthContext';
import ReferralProgram from '../components/ReferralProgram';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GetAppIcon from '@mui/icons-material/GetApp';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeTab, setActiveTab] = React.useState<'overview' | 'friends' | 'achievements' | 'referrals'>('overview');
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsAppInstalled(true);
      }
    };
    
    checkIfInstalled();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsAppInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  // Mock data
  const farms = [
    { id: 1, name: 'Urban Oasis', avatar: { type: 'shape' as const, data: 'hexagon:gradient:#22c55e:#06b6d4:45' }, role: 'Owner' },
    { id: 2, name: 'Community Garden', avatar: { type: 'shape' as const, data: 'circle:solid:#f59e0b' }, role: 'Member' },
    { id: 3, name: 'Rooftop Paradise', avatar: { type: 'shape' as const, data: 'leaf:gradient:#84cc16:#22c55e:90' }, role: 'Member' },
  ];

  const friends = [
    { id: 1, name: 'Sarah Chen', username: 'sarahgreen', avatar: '🌸', streak: 15 },
    { id: 2, name: 'Mike Johnson', username: 'plantdad', avatar: '🌿', streak: 8 },
    { id: 3, name: 'Emma Wilson', username: 'sunflowergirl', avatar: '🌻', streak: 23 },
    { id: 4, name: 'David Park', username: 'veggieking', avatar: '🥕', streak: 5 },
    { id: 5, name: 'Lisa Brown', username: 'herbwhisperer', avatar: '🌿', streak: 12 },
    { id: 6, name: 'Tom Davis', username: 'gardenguru', avatar: '🌳', streak: 30 },
  ];

  const badges = [
    { id: 1, name: 'January Champion', icon: '🏆', date: 'Jan 2025' },
    { id: 2, name: 'Green Thumb', icon: '👍', date: 'Dec 2024' },
    { id: 3, name: 'Harvest Master', icon: '🌾', date: 'Nov 2024' },
  ];

  const achievements = [
    { id: 1, name: 'First Plant', icon: '🌱', unlocked: true },
    { id: 2, name: '7 Day Streak', icon: '🔥', unlocked: true },
    { id: 3, name: '100 XP', icon: '💯', unlocked: true },
    { id: 4, name: 'Social Butterfly', icon: '🦋', unlocked: true },
    { id: 5, name: 'Master Gardener', icon: '🌳', unlocked: false },
    { id: 6, name: 'Harvest King', icon: '👑', unlocked: false },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 10 }}>
      {/* Profile Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', pt: 3, pb: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center" gap={3}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'white', color: 'primary.main', fontSize: '2.5rem' }}>
                🌱
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {user?.username || 'GardenLover'}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <CalendarTodayIcon sx={{ fontSize: 16 }} />
                  <Typography variant="body2">
                    Joined {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton sx={{ color: 'white' }}>
              <EditIcon />
            </IconButton>
          </Box>

          {/* Stats */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="h6">{friends.filter(f => f.id <= 4).length}</Typography>
              <Typography variant="body2">Following</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6">127</Typography>
              <Typography variant="body2">Followers</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -2 }}>
        {/* Farms */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              My Farms
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              {farms.map(farm => (
                <Chip
                  key={farm.id}
                  icon={<FarmAvatar avatar={farm.avatar} size={24} />}
                  label={`${farm.name} (${farm.role})`}
                  variant="outlined"
                  sx={{ pl: 0.5, cursor: 'pointer' }}
                  onClick={() => navigate(`/farms/${farm.id}/manage`)}
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Install App Button */}
        {!isAppInstalled && deferredPrompt && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Install Botala App
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get the full experience with offline access and notifications
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<GetAppIcon />}
                  onClick={handleInstallClick}
                  sx={{ minWidth: 120 }}
                >
                  Install App
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Add Friends */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Add Friends
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, username, phone, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button startIcon={<PersonAddIcon />} size="small">
                      Add
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Box display="flex" gap={2} mb={2} sx={{ overflowX: 'auto' }}>
          <Button 
            variant={activeTab === 'overview' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </Button>
          <Button 
            variant={activeTab === 'friends' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('friends')}
          >
            Friends
          </Button>
          <Button 
            variant={activeTab === 'achievements' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('achievements')}
          >
            Achievements
          </Button>
          <Button 
            variant={activeTab === 'referrals' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('referrals')}
          >
            Referrals
          </Button>
        </Box>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Stats Overview
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <LocalFireDepartmentIcon color="error" sx={{ fontSize: 40 }} />
                      <Typography variant="h5">{user?.gamification?.streak || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Daily Streak</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4">⭐</Typography>
                      <Typography variant="h5">{user?.gamification?.xp || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Total XP</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <EmojiEventsIcon color="primary" sx={{ fontSize: 40 }} />
                      <Typography variant="h5">Gold</Typography>
                      <Typography variant="body2" color="text.secondary">League</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4">🏅</Typography>
                      <Typography variant="h5">{badges.length}</Typography>
                      <Typography variant="body2" color="text.secondary">Badges</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Friend Streaks */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Friend Streaks
                </Typography>
                <List>
                  {friends.slice(0, 6).map((friend, index) => (
                    <ListItem key={friend.id} dense>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'grey.200' }}>
                          <Typography>{friend.avatar}</Typography>
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={friend.name}
                        secondary={`@${friend.username}`}
                      />
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalFireDepartmentIcon color="error" sx={{ fontSize: 20 }} />
                        <Typography variant="body2">{friend.streak}</Typography>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Monthly Badges */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Monthly Badges
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {badges.map(badge => (
                    <Box key={badge.id} textAlign="center">
                      <Typography variant="h3">{badge.icon}</Typography>
                      <Typography variant="caption">{badge.date}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </>
        )}

        {/* Friends Tab */}
        {activeTab === 'friends' && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Following ({friends.length})
              </Typography>
              <List>
                {friends.map(friend => (
                  <ListItem key={friend.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'grey.200' }}>
                        <Typography>{friend.avatar}</Typography>
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={friend.name}
                      secondary={`@${friend.username}`}
                    />
                    <Button size="small" variant="outlined">
                      Following
                    </Button>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Achievements
              </Typography>
              <Grid container spacing={2}>
                {achievements.map(achievement => (
                  <Grid item xs={4} sm={3} key={achievement.id}>
                    <Box 
                      textAlign="center" 
                      sx={{ 
                        opacity: achievement.unlocked ? 1 : 0.3,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: achievement.unlocked ? 'primary.light' : 'grey.100'
                      }}
                    >
                      <Typography variant="h2">{achievement.icon}</Typography>
                      <Typography variant="caption">{achievement.name}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <ReferralProgram />
        )}
      </Container>
    </Box>
  );
};

export default Profile;